import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Calendar, ArrowLeft, Lock, Shield, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chart ID to animal mapping (obscured codes)
const CHART_ID_TO_ANIMAL = {
    'x7k2m': 'rat', 'p3n9q': 'ox', 'v8j4w': 'tiger', 'r2f6y': 'rabbit', 
    'h5c1z': 'dragon', 'b9t3x': 'snake', 'n4g8k': 'horse', 'm1d7p': 'goat', 
    'q6s2v': 'monkey', 'w3h9n': 'rooster', 'y8b4j': 'dog', 'c5r1f': 'pig'
};
const ANIMAL_TO_CHART_ID = {
    'rat': 'x7k2m', 'ox': 'p3n9q', 'tiger': 'v8j4w', 'rabbit': 'r2f6y', 
    'dragon': 'h5c1z', 'snake': 'b9t3x', 'horse': 'n4g8k', 'goat': 'm1d7p', 
    'monkey': 'q6s2v', 'rooster': 'w3h9n', 'dog': 'y8b4j', 'pig': 'c5r1f'
};

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

function AdminLoginPrompt() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center" data-testid="admin-login-prompt">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Admin Access Required</h1>
                <p className="text-slate-500 mb-6">Please log in with an admin account to view all charts</p>
                
                <Link 
                    to="/login" 
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                    data-testid="admin-login-link"
                >
                    <LogIn className="w-5 h-5" />
                    Log In
                </Link>
                
                <p className="text-slate-400 text-sm mt-4">
                    Don't have an account? <Link to="/register" className="text-amber-600 hover:underline">Register</Link>
                </p>
            </div>
        </div>
    );
}

function NotAdminMessage() {
    const navigate = useNavigate();
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center" data-testid="not-admin-message">
                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h1>
                <p className="text-slate-500 mb-6">Your account does not have admin privileges</p>
                
                <button 
                    onClick={() => navigate('/energy-chart')}
                    className="inline-flex items-center gap-2 bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                    Go to Energy Chart
                </button>
            </div>
        </div>
    );
}

function AnimalCard({ animal, onClick }) {
    return (
        <button
            onClick={onClick}
            className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
            data-testid={`admin-animal-card-${animal.id}`}
        >
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-3xl font-bold mb-4 group-hover:scale-110 transition-transform">
                {animal.chinese}
            </div>
            <div className="text-center">
                <div className="font-bold text-slate-800 text-lg">{animal.english}</div>
                <div className="text-slate-400 text-sm">{animal.pinyin}</div>
                <div className="text-slate-400 text-xs mt-2">
                    {animal.years.slice(-3).join(', ')}...
                </div>
            </div>
        </button>
    );
}

function AnimalGrid({ animals, onSelect }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {animals.map(animal => (
                <AnimalCard key={animal.id} animal={animal} onClick={() => onSelect(animal.id)} />
            ))}
        </div>
    );
}

function AnimalSelector({ animals, user, onLogout }) {
    const navigate = useNavigate();
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="admin-animal-selector">
            <div className="max-w-4xl mx-auto">
                <div className="flex justify-between items-start mb-8">
                    <div className="flex-1 text-center">
                        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                            <Shield className="w-4 h-4" />
                            Admin Panel
                        </div>
                        <h1 className="text-4xl font-bold text-slate-800 mb-2">
                            生肖能量圖
                        </h1>
                        <p className="text-slate-400 text-xl">All Charts</p>
                        <p className="text-slate-500 mt-2">Select any animal to view its energy chart</p>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-slate-500 mb-1">{user?.name || user?.email}</p>
                        <div className="flex flex-col gap-1">
                            <button 
                                onClick={() => navigate('/admin/dashboard')} 
                                className="text-sm text-amber-600 hover:text-amber-700 underline"
                                data-testid="admin-dashboard-link"
                            >
                                User Management
                            </button>
                            <button onClick={onLogout} className="text-sm text-slate-500 hover:text-slate-700 underline" data-testid="admin-logout-btn">
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
                
                <AnimalGrid animals={animals} onSelect={(id) => navigate(`/admin/${ANIMAL_TO_CHART_ID[id]}`)} />
            </div>
        </div>
    );
}

function SimpleBar({ day, index, animated }) {
    const [height, setHeight] = useState(0);
    const targetHeight = Math.max(12, (day.energy / 100) * 200);
    
    useEffect(() => {
        if (animated) {
            const timer = setTimeout(() => setHeight(targetHeight), index * 30);
            return () => clearTimeout(timer);
        }
        setHeight(targetHeight);
    }, [animated, index, targetHeight]);
    
    const color = day.energy >= 80 ? 'bg-emerald-500' : day.energy < 30 ? 'bg-red-500' : 'bg-blue-500';
    const textColor = day.energy >= 80 ? 'text-emerald-600' : day.energy < 30 ? 'text-red-600' : 'text-blue-600';
    
    return (
        <div className="flex flex-col items-center justify-end flex-1 min-w-0" style={{ height: '240px' }}>
            <span className={`text-[10px] font-semibold mb-1 ${textColor}`}>{Math.round(day.energy)}%</span>
            <div className={`w-[85%] ${color} rounded-t`} style={{ height: `${height}px`, transition: 'height 0.5s ease-out' }} />
            <span className="text-[10px] text-slate-500 mt-1">{day.day}</span>
        </div>
    );
}

function ChartBars({ data, animated }) {
    return (
        <div className="flex items-end gap-[2px] pb-4">
            {data.map((day, i) => <SimpleBar key={day.day} day={day} index={i} animated={animated} />)}
        </div>
    );
}

function AdminChartView({ animal, animals, onBack }) {
    const navigate = useNavigate();
    const [year, setYear] = useState(2026);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [animated, setAnimated] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/tong-shu/energy-chart/${animal}/${year}/${month}`);
            if (res.ok) setChartData(await res.json());
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }, [animal, year, month]);

    useEffect(() => { fetchData(); }, [fetchData]);
    
    useEffect(() => {
        setAnimated(false);
        const t = setTimeout(() => setAnimated(true), 50);
        return () => clearTimeout(t);
    }, [animal, year, month]);

    const goPrev = () => month === 1 ? (setMonth(12), setYear(y => y - 1)) : setMonth(m => m - 1);
    const goNext = () => month === 12 ? (setMonth(1), setYear(y => y + 1)) : setMonth(m => m + 1);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="admin-chart-view">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="admin-back-btn">
                        <ArrowLeft className="w-5 h-5" />
                        <span>All Animals</span>
                    </button>
                    
                    <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                        <Shield className="w-4 h-4" />
                        Admin View
                    </div>
                    
                    <select value={animal} onChange={(e) => navigate(`/admin/${ANIMAL_TO_CHART_ID[e.target.value]}`)} className="px-4 py-2 border rounded-lg bg-white shadow-sm">
                        {animals.map(a => <option key={a.id} value={a.id}>{a.chinese} {a.english}</option>)}
                    </select>
                </div>

                {chartData && (
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-3xl font-bold">
                                {chartData.animal.chinese}
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-800">{chartData.animal.english}</h1>
                                <p className="text-slate-500">Birth Years: {chartData.animal.years.slice(-4).join(', ')}...</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                        <button onClick={goPrev} disabled={year <= 2024 && month <= 1} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50">
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <div className="flex items-center gap-4">
                            <Calendar className="w-5 h-5 text-slate-400" />
                            <h2 className="text-xl font-semibold text-slate-800">{MONTH_NAMES[month - 1]} {year}</h2>
                        </div>
                        <button onClick={goNext} disabled={year >= 2030 && month >= 12} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50">
                            <ChevronRight className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {loading && (
                    <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-slate-500">Loading...</p>
                    </div>
                )}

                {chartData && !loading && (
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <ChartBars data={chartData.data} animated={animated} />
                        <div className="text-center text-slate-500 text-sm">Day</div>
                        
                        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-slate-800">{chartData.statistics.average}%</div>
                                <div className="text-sm text-slate-500">Average</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-emerald-600">Day {chartData.statistics.peak_day.day}</div>
                                <div className="text-sm text-slate-500">Peak ({chartData.statistics.peak_day.energy}%)</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-red-600">Day {chartData.statistics.low_day.day}</div>
                                <div className="text-sm text-slate-500">Lowest ({chartData.statistics.low_day.energy}%)</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Legend</h3>
                    <div className="flex flex-wrap gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-4 bg-emerald-500 rounded"></div>
                            <span className="text-sm text-slate-600">High (80%+)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-4 bg-blue-500 rounded"></div>
                            <span className="text-sm text-slate-600">Average (30-79%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-4 bg-red-500 rounded"></div>
                            <span className="text-sm text-slate-600">Low (&lt;30%)</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function AdminEnergyChart() {
    const { chartId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user, token, logout } = useAuth();
    const [isAdmin, setIsAdmin] = useState(false);
    const [checkingAdmin, setCheckingAdmin] = useState(true);
    const [animals, setAnimals] = useState([]);
    
    // Convert chartId to animal name for API calls
    const animal = chartId ? CHART_ID_TO_ANIMAL[chartId] : null;

    // Check if user is admin
    useEffect(() => {
        const checkAdmin = async () => {
            if (!isAuthenticated || !token) {
                setCheckingAdmin(false);
                return;
            }
            
            try {
                const res = await fetch(`${API_URL}/api/auth/admin/check`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setIsAdmin(data.is_admin);
                }
            } catch (err) {
                console.error('Failed to check admin status:', err);
            }
            setCheckingAdmin(false);
        };
        
        checkAdmin();
    }, [isAuthenticated, token]);

    // Fetch animals list
    useEffect(() => {
        if (!isAdmin) return;
        
        fetch(`${API_URL}/api/tong-shu/energy-chart/animals`)
            .then(r => r.ok ? r.json() : null)
            .then(d => d && setAnimals(d.animals || []))
            .catch(console.error);
    }, [isAdmin]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Loading state
    if (checkingAdmin) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    // Not logged in
    if (!isAuthenticated) {
        return <AdminLoginPrompt />;
    }

    // Not admin
    if (!isAdmin) {
        return <NotAdminMessage />;
    }

    // Admin - show content
    if (!chartId) {
        return <AnimalSelector animals={animals} user={user} onLogout={handleLogout} />;
    }

    return <AdminChartView animal={animal} animals={animals} onBack={() => navigate('/admin')} />;
}
